// `extension-alert-candidates-message` and the `content/` component it wraps — the email
// composer that fills a side view on four routes.
//
// ⚠️ WHAT IS HERE IS THE COMPOSER, NOT THE SENDING. Drafts, templates, signatures and the send
// itself are a service layer of their own — six APIs, a parameter validator, a draft autosave
// and two products' worth of filters — and `Send` mails real candidates. This renders the
// screen, holds what the user types, and stops at the edge of every outward-facing write. The
// three captured routes are all in the resting state, which is exactly what this covers.
//
// The signature editor follows upstream woo@10e2fe0302 (HubSpot 49214431098): no blur autosave,
// locked until the signature loads, unsaved edits marked and blocking Send, CANCEL restoring,
// and unknown `{tokens}` refused with an inline error. SAVE is the one signature write: it runs
// those checks, confirms a clear, and hands the POST to the host — see `saveSignature` below.
import { createElement, useRef, useState, type ReactElement, type ReactNode } from 'react'
import { DAILY_SEND_LIMIT_COPY, canSendMessage, disabledSendButtonCopy, type SendPreconditions } from './canSendMessage.ts'
import { UibTooltipPopup, useHoverTooltip } from '../common/UibTooltip.tsx'
import { checkSignatureParameterValidity } from './messageParamValidation.ts'
import {
  acceptsSignatureChange,
  afterSignatureSave,
  invalidSignatureParametersMessage,
  isSignatureDirty,
  planSignatureSave,
  SIGNATURE_SAVE_FAILED_MESSAGE,
  type SignatureLoadState,
} from './signatureEditing.ts'
import { GenericDialogue } from './ShortlistDialogue.tsx'
import { RichTextEditor } from '../common/RichTextEditor.tsx'
import {
  BODY_ALERTLESS_TOOLBAR,
  BODY_TOOLBAR,
  RichTextToolbar,
  SIGNATURE_TOOLBAR,
} from '../common/RichTextToolbar.tsx'

/**
 * `numberSuffix` (`common/filters/number-suffix.filter.ng1.ts`) — "1st", "2nd", "3rd", "4th".
 *
 * ⚠️ ELEVEN TO THIRTEEN ARE "th", AND THE CHECK COMES FIRST. 11 ends in 1 and is "11th", not
 * "11st"; the same for 12 and 13, and again at 111-113. A rule written on the last digit alone
 * is right for 90% of a follow-up chain and wrong at the point nobody tests.
 */
export function numberSuffix(value: number): string {
  const lastTwo = value % 100
  if (lastTwo >= 11 && lastTwo <= 13) return `${String(value)}th`
  const last = value % 10
  if (last === 1) return `${String(value)}st`
  if (last === 2) return `${String(value)}nd`
  if (last === 3) return `${String(value)}rd`
  return `${String(value)}th`
}

/** `ITempExtensionMessageFollowupData`. */
export interface MessageFollowup {
  body?: string
  daysUntilSend?: number
  isEditing?: boolean
  isSaved?: boolean
}

/**
 * `getTempFollowups` with nothing loaded — ONE entry, empty and neither editing nor saved,
 * which is what renders the "No 1st follow-up set" bar. An empty ARRAY renders no bar at all,
 * and the bar is 46 pixels tall on every capture of this screen.
 */
export function defaultFollowups(): MessageFollowup[] {
  return [{ body: '', isEditing: false, isSaved: false }]
}

/** `IShortlistEntityCheckboxDetails`, as the To field reads it. */
export interface MessageRecipient {
  id: number
  name?: string | null
}

export interface MessageComposerProps {
  /** False until the draft, signature and templates have all settled. */
  isLoaded?: boolean
  recipients?: readonly MessageRecipient[]
  /** `ctrl.subject` — also the TITLE once it has a value ("New Message" until then). */
  subject?: string
  body?: string
  signature?: string
  /**
   * `signatureLoadState` (upstream woo@10e2fe0302). ⚠️ THE EDITOR IS LOCKED UNTIL THIS IS
   * `'loaded'` — a failed load must not look like "no signature", because an empty editor that
   * can be opened is an empty editor that can be saved over the stored one (HubSpot 49214431098).
   * Defaults to `'loading'`, upstream's initial value, so an unwired host gets a locked editor.
   */
  signatureLoadState?: SignatureLoadState
  /** `onRetryLoadSignature` — the RETRY on "Couldn't load your signature." */
  onRetryLoadSignature?: () => void
  followups?: readonly MessageFollowup[]
  /**
   * ⚠️ PRESENT MEANS "FROM AN ALERT", AND IT CHANGES THE TOOLBAR. With an alert the body strip
   * offers `{Hunter}` as well as `{First name}`; without one it does not, because there is no
   * hunter to name. It also decides whether recipients can be removed.
   */
  alertId?: number
  /** `sessionInfo.viaHunter` — chips get an ✕ and the count collapses behind a summary. */
  viaHunter?: boolean
  /**
   * Which product's composer this is.
   *
   * ⚠️ THE TWO TEMPLATES ARE THE SAME FILE'S WORTH OF MARKUP. The contacts composer renders
   * `shortlist-poc/common/messages/extension-alert-entity-messages.tpl.html`, which is the
   * candidate template with `Candidate` renamed to `Entity` throughout and ONE addition — the
   * daily-send-limit line below. So this takes an entity rather than growing a second
   * component, and the entity picks the host element type and the noun in
   * `getEntityNameText()`.
   */
  entity?: 'candidates' | 'clientContacts'
  /**
   * `isDailySendLimitReached()` — `remainingDailyEntities <= 0 && checkedEntities.length > 0`.
   *
   * ⚠️ IT REPLACES THE "N selected" COUNT RATHER THAN SITTING BESIDE IT. When the limit is
   * reached the whole count span is swapped for the copy, so the two can never both show. The
   * candidate template has no such branch at all — it is the one thing the shared entity
   * template adds, and it is why that template is not simply the candidate one renamed.
   */
  dailySendLimitReached?: boolean
  /** `flex-basis` on the component element — Extended at every call site. */
  flexBasis?: number
  onClose?: () => void
  /**
   * ⚠️ THIS MAILS REAL CANDIDATES, and unlike an ATS record a sent message cannot be
   * withdrawn. The composer calls it only when `canSendMessage` holds — see that function for
   * the five conjuncts — and the host decides what to do with the outcome.
   */
  onSend?: () => void
  /**
   * `onSendButtonClick`'s first line — the press itself, BEFORE the composer decides whether it
   * can send. Upstream records every press, the faded button's included; `onSend` is only the
   * presses that pass.
   */
  onSendButtonClick?: () => void
  /** `validAndAvailableEntitiesToSend.length` — see `canSendMessage`; NOT `recipients.length`. */
  sendableCount?: number
  /** `remainingDailyEntities`. */
  remainingDaily?: number
  /** `invalidParameters.length` — unresolved `{{placeholders}}`. */
  invalidParameterCount?: number
  /** `isEditingFollowup`. */
  editingFollowup?: boolean
  /** True while a send is in flight, so a second click cannot double-mail. */
  sending?: boolean
  /**
   * What to say when the send did not happen, or did not reach everyone.
   *
   * ⚠️ UPSTREAM HAS NO SUCH ELEMENT, AND THAT IS NOT A REASON TO LEAVE IT OUT. It reports both
   * outcomes in a modal — `showMessageSentDialogue(candidateAmount, blacklistedAmount)`
   * (`shortlist.candidate-message.service.ng1.ts:106`) — which is a template, an auto-close
   * timer and a dialogue service this port does not have yet. The rule this repo already
   * applies to the other two outward writes stands: a write with no visible outcome is worse
   * than no write, and mail cannot be withdrawn. So the host gets somewhere to say "the server
   * refused" or "three of twelve are blacklisted", in upstream's own footer error label, until
   * the dialogue is ported.
   *
   * Rendered only when set, so every reference capture — which never sets it — is unchanged.
   */
  error?: string
  onSubjectChange?: (subject: string) => void
  onBodyChange?: (html: string) => void
  /**
   * Every keystroke in the signature editor, AND the restore on CANCEL — which writes back the
   * signature as it was when the editor opened. The host's value is what Send mails, and Send is
   * blocked while it differs from that saved one, so it can only ever go out saved.
   */
  onSignatureChange?: (html: string) => void
  /**
   * `onSaveSignature({ clear })` — the POST. The host sends `{ signature: clear ? null :
   * signature, clear }`; a rejection keeps the editor open with upstream's save-failure message.
   * Absent, SAVE validates and the editor stays open, which is how every capture renders.
   */
  onSaveSignature?: (clear: boolean) => Promise<void>
  /**
   * `canSaveTemplate` — false until there is something worth saving. Upstream computes it from
   * the subject, the body and the parameter validator; the composer opens with all three empty,
   * which is the state every capture is in.
   */
  canSaveTemplate?: boolean
  /** The attachment list, when there is one; it arrives with the upload flow. */
  attachments?: ReactNode
}

export function MessageComposer({
  isLoaded = false,
  recipients = [],
  subject = '',
  body = '',
  signature = '',
  signatureLoadState = 'loading',
  onRetryLoadSignature,
  followups = defaultFollowups(),
  alertId,
  viaHunter = false,
  entity = 'candidates',
  dailySendLimitReached = false,
  onSend,
  onSendButtonClick,
  sendableCount,
  remainingDaily,
  invalidParameterCount,
  editingFollowup,
  sending = false,
  error,
  flexBasis,
  onClose,
  onSubjectChange,
  onBodyChange,
  onSignatureChange,
  onSaveSignature,
  canSaveTemplate = false,
  attachments,
}: MessageComposerProps): ReactElement {
  // ⚠️ DEFAULTS CHOSEN SO AN UNWIRED HOST CANNOT SEND. `sendableCount` and `remainingDaily`
  // default to 0, so a caller that renders the composer without passing them gets a Send that
  // is inert and looks it — which is the state every reference capture is in. Defaulting them
  // to a permissive value would arm the button for every existing caller at once.
  const [isSubjectInFocus, setSubjectInFocus] = useState(false)
  const [isEditingSignature, setEditingSignature] = useState(false)
  // `savedSignature` — what CANCEL restores and what unsaved edits are measured against.
  const [savedSignature, setSavedSignature] = useState<string>('')
  const [signatureErrorMessage, setSignatureErrorMessage] = useState<string | null>(null)
  const [isSavingSignature, setSavingSignature] = useState(false)
  // The "Remove your signature?" dialogue — upstream's `showGenericDialogue` in front of a clear.
  const [isConfirmingClear, setConfirmingClear] = useState(false)
  // ⚠️ THE EDITOR'S TEXT NOW, NOT WHEN SAVE WAS CLICKED. A closure over `signature` reads the
  // value at the click, so after the POST it could never see edits typed while it was in flight
  // — which is exactly what `afterSignatureSave` has to compare (woo@30b5a7d45c).
  const latestSignature = useRef(signature)
  latestSignature.current = signature
  const signatureDirty = isSignatureDirty(isEditingSignature, signature, savedSignature)

  const sendPreconditions: SendPreconditions = {
    subject,
    body,
    recipientCount: recipients.length,
    sendableCount: sendableCount ?? 0,
    remainingDaily: remainingDaily ?? 0,
    invalidParameterCount: invalidParameterCount ?? 0,
    ...(editingFollowup === undefined ? {} : { editingFollowup }),
    unsavedSignature: signatureDirty,
  }
  const sendable = canSendMessage(sendPreconditions)
  // `uib-tooltip="{{ctrl.calculateDisabledSendButtonCopy()}}" tooltip-enable="!ctrl.canSendMessage"`.
  // ⚠️ THE COPY READS THE CAP AS THE HOST GAVE IT, NOT DEFAULTED TO 0: an unfetched cap is
  // upstream's `undefined`, which is "not reached" — see `isDailySendLimitReached`.
  const sendCopy = disabledSendButtonCopy({ ...sendPreconditions, remainingDaily })
  const sendTooltip = useHoverTooltip(sendCopy, !sendable)
  const sendHost = useRef<HTMLDivElement>(null)

  /** `openSignatureEdit` — a no-op until the signature has loaded. */
  const openSignatureEdit = (): void => {
    if (isEditingSignature || signatureLoadState !== 'loaded') return
    setSavedSignature(signature)
    setEditingSignature(true)
  }

  /** `finishSignatureEdit` — close, with the editor showing `value`. */
  const finishSignatureEdit = (value: string): void => {
    setSavedSignature(value)
    if (value !== signature) onSignatureChange?.(value)
    setSignatureErrorMessage(null)
    setEditingSignature(false)
  }

  /**
   * `onSignatureChange`. The live re-validation only REPLACES an error already showing — the
   * error first appears on SAVE, as upstream's does, not on the first stray brace typed. An
   * update landing after the editor closed is dropped — see `acceptsSignatureChange`. Upstream
   * also writes the saved value back over its model there; the host's value here was never
   * changed, and `RichTextEditor` restores its own view of it when disabled.
   */
  const changeSignature = (html: string): void => {
    if (!acceptsSignatureChange(isEditingSignature, isSavingSignature)) return
    onSignatureChange?.(html)
    if (signatureErrorMessage !== null) {
      setSignatureErrorMessage(invalidSignatureParametersMessage(checkSignatureParameterValidity(html)))
    }
  }

  /** `closeSignatureEdit` — CANCEL. Refused while a save is in flight, as upstream's is. */
  const closeSignatureEdit = (): void => {
    if (isSavingSignature) return
    finishSignatureEdit(savedSignature)
  }

  /**
   * `saveSignature()`. Unknown tokens keep the editor open with the inline error; an editor that
   * was empty and is still empty just closes; emptying a saved signature asks first; anything
   * else is POSTed.
   */
  const saveSignature = (): void => {
    if (isSavingSignature) return
    const plan = planSignatureSave(signature, savedSignature)
    if (plan.kind === 'invalid') setSignatureErrorMessage(plan.message)
    else if (plan.kind === 'close') finishSignatureEdit(savedSignature)
    else if (plan.clear) setConfirmingClear(true)
    else void writeSignature(false)
  }

  /**
   * The POST and what follows it. ⚠️ WITH NO HOST HANDLER THERE IS NO WRITE AND THE EDITOR STAYS
   * OPEN: closing it would show a signature as saved that the server never received.
   */
  const writeSignature = async (clear: boolean): Promise<void> => {
    if (onSaveSignature === undefined) return
    const submitted = signature
    setSignatureErrorMessage(null)
    setSavingSignature(true)
    try {
      await onSaveSignature(clear)
    } catch {
      setSignatureErrorMessage(SIGNATURE_SAVE_FAILED_MESSAGE)
      return
    } finally {
      setSavingSignature(false)
    }
    const next = afterSignatureSave(submitted, latestSignature.current, clear)
    if (next.kind === 'keep-open') setSavedSignature(next.saved)
    else finishSignatureEdit(next.saved)
  }

  const content = !isLoaded ? (
    <div className="extension-candidate-messages-outer-container">
      <div className="extension-candidate-messages-loading-container">
        <i className="fa fa-spinner fa-spin fa-lg" />
      </div>
    </div>
  ) : (
    <div className="extension-candidate-messages-outer-container">
      <div className="extension-candidate-message-template-all-container">
        <div className="extension-candidate-messages-title-container">
          <div className="extension-candidate-messages-form-x-icon">
            <i
              className="far fa-times"
              role="button"
              aria-label="Close"
              tabIndex={0}
              onClick={onClose}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') onClose?.()
              }}
            />
          </div>
          {/* ⚠️ THE TITLE IS THE SUBJECT ONCE THERE IS ONE. "New Message" is a fallback, not a
              heading — type a subject and this line becomes it, truncated. */}
          <div className="extension-candidate-messages-form-title text-left truncate" dir="auto">
            {subject === '' ? 'New Message' : subject}
          </div>
        </div>
        <div className="extension-candidate-message-template-autocomplete-container">
          <input
            name="currentTemplate"
            className="form-control input extension-candidate-message-template-input"
            type="text"
            placeholder="Search for templates"
            autoComplete="off"
            readOnly
          />
          <i className="fas fa-search" />
          <i className="far fa-times" />
        </div>
        <div className="extension-candidate-messages-container">
          <div className="extension-candidate-messages-header">
            <div className="extension-candidate-messages-form-to">
              {/* The trailing space after "To:" is upstream's and it is inside the element. */}
              <div className="email-form-section-text">To: </div>
              <div id="email-form-recipients" className="email-form-recipients" tabIndex={-1}>
                {/* ⚠️ THE CHIPS ARE HIDDEN **ON THE HUNTER SCREENS**, not shown there. The
                    condition is `showAllRecipients || !viaHunter`: a review screen with fifty
                    selected candidates shows a count, and a profile — which is not via hunter —
                    lists them. The reading that renders chips everywhere fills the review
                    screen's To field with fifty names. */}
                {!viaHunter
                  ? recipients.map((recipient) => (
                      <span className="email-form-recipients-name-container" key={recipient.id} title={recipient.name ?? ''}>
                        <span className="email-form-recipients-name">{recipient.name}</span>
                      </span>
                    ))
                  : null}
              </div>
              {recipients.length === 0 ? null : (
                <div className="email-form-number-selected">
                  {dailySendLimitReached ? (
                    <span className="email-form-daily-limit-reached">
                      {DAILY_SEND_LIMIT_COPY}
                    </span>
                  ) : (
                    <span>{`${String(recipients.length)} selected`}</span>
                  )}
                </div>
              )}
            </div>
            <div className="extension-candidate-messages-form-subject">
              <div className="email-form-section-subject">
                <input
                  id="extension-message-email-subject"
                  type="text"
                  dir="auto"
                  name="emailSubject"
                  maxLength={1000}
                  required
                  placeholder="Subject:"
                  value={subject}
                  onFocus={() => {
                    setSubjectInFocus(true)
                  }}
                  onBlur={() => {
                    setSubjectInFocus(false)
                  }}
                  onChange={(event) => {
                    onSubjectChange?.(event.target.value)
                  }}
                />
              </div>
            </div>
          </div>
          <div className="extension-candidate-messages-form-body">
            <div className="email-form-section-body">
              {/* ⚠️ THE CONTENT COMPONENT'S ENTIRE STYLESHEET IS SCOPED UNDER THIS ELEMENT —
                  `extension-alert-candidates-messages-content { display: block; .candidate-
                  message-content { … } }`. Rendering the inner div alone loses every rule in
                  286 lines: the editors keep bootstrap's `.form-control` border, the 300px
                  textAngular minimum is never overridden, and the toolbar row is pushed off
                  the bottom of the panel. Tenth time an element type has carried the rules. */}
              {createElement(
                'extension-alert-candidates-messages-content',
                null,
                <div className="candidate-message-content">
                <div className="candidate-message-template-area nicer-scroller four-px-scroll">
                  <RichTextEditor
                    id="candidate-message-body"
                    className="candidate-message-body"
                    editorClassName="candidate-message-body-editor nicer-scroller"
                    placeholder="Compose a message"
                    // `ta-default-wrap="div"`: what Send mails is `<div>…</div>`, not the bare text.
                    defaultWrap="div"
                    html={body}
                    onChange={onBodyChange}
                  />
                  {attachments}
                  {followups.map((followup, index) => (
                    <div
                      // eslint-disable-next-line react/no-array-index-key -- `ng-repeat` is positional, and the ordinal IS the index.
                      key={index}
                      className={`candidate-message-followup-container${followup.isEditing === true ? ' candidate-message-followup-container-edit' : ''}`}
                    >
                      <div className="candidate-message-followup-header">
                        {followup.isEditing === true || followup.isSaved === true ? null : (
                          <div className="no-followup-set">
                            <span className="no-follow-up-set-text">
                              <i className="fa fa-exclamation-circle" />
                              &nbsp; {`No ${numberSuffix(index + 1)} follow-up set`}
                            </span>
                            <span className="no-followup-set-button">
                              <button type="button">Create follow-up</button>
                            </span>
                          </div>
                        )}
                        {followup.isSaved !== true ? null : (
                          <div className="followup-saved">
                            <span className="followup-saved-text">
                              <i className="fa fa-check-circle" />
                              &nbsp; {`${numberSuffix(index + 1)} Follow-up email saved`}
                            </span>
                            <span className="followup-saved-actions">
                              <i className="fa fa-pencil cursor-pointer" />
                              <i className="fas fa-trash-alt cursor-pointer" />
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  <div className="candidate-message-signature-container">
                    <div
                      className={`candidate-message-signature-edit ${isEditingSignature ? 'candidate-message-signature-editing' : 'candidate-message-signature-view'}`}
                    >
                      <RichTextEditor
                        id="candidate-message-signature"
                        className="candidate-message-signature"
                        editorClassName="candidate-message-signature-editor nicer-scroller"
                        placeholder="Add your signature"
                        defaultWrap="div"
                        html={signature}
                        // ⚠️ THE `ng-blur` AUTOSAVE IS GONE UPSTREAM (woo@10e2fe0302) AND IS NOT
                        // HERE EITHER: clicking away posted whatever the editor held, including an
                        // editor still empty because the signature had not loaded. Only SAVE writes.
                        disabled={!isEditingSignature || isSavingSignature || signatureLoadState !== 'loaded'}
                        onChange={changeSignature}
                        onClick={openSignatureEdit}
                      />
                      <div className="signature-edit-actions">
                        {signatureLoadState !== 'loading' ? null : (
                          <div className="signature-load-state">Loading your signature&hellip;</div>
                        )}
                        {signatureLoadState !== 'failed' ? null : (
                          <div className="signature-load-state has-error">
                            Couldn&apos;t load your signature.{' '}
                            <span
                              className="signature-load-retry"
                              role="button"
                              tabIndex={0}
                              onClick={onRetryLoadSignature}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') onRetryLoadSignature?.()
                              }}
                            >
                              RETRY
                            </span>
                          </div>
                        )}
                        {isEditingSignature || signatureLoadState !== 'loaded' ? null : (
                          <div className="signature-view-edit-button">
                            <span
                              title="Edit signature"
                              role="button"
                              tabIndex={0}
                              onClick={openSignatureEdit}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') openSignatureEdit()
                              }}
                            >
                              <i className="fa fa-pencil cursor-pointer" />
                            </span>
                          </div>
                        )}
                        {!isEditingSignature ? null : (
                          <div className="signature-view-cancel-save">
                            {signatureDirty ? <span className="signature-unsaved">Unsaved changes</span> : null}
                            <span
                              className="signature-cancel"
                              role="button"
                              tabIndex={0}
                              onClick={closeSignatureEdit}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') closeSignatureEdit()
                              }}
                            >
                              CANCEL
                            </span>
                            {/* `click-and-disable`: inert while a save is in flight — see `saveSignature`. */}
                            <span
                              className="signature-save"
                              role="button"
                              tabIndex={0}
                              onClick={saveSignature}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') saveSignature()
                              }}
                            >
                              SAVE
                            </span>
                          </div>
                        )}
                        {signatureErrorMessage === null ? null : (
                          <label className="control-label has-error extension-message-invalid-param-error signature-error">
                            {signatureErrorMessage}
                          </label>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="candidate-message-toolbar-area">
                  {/* ⚠️ THREE MUTUALLY EXCLUSIVE STRIPS SHARE ONE ROW, and which one is showing
                      is a property of what has FOCUS. Editing the signature swaps in the
                      signature strip; focusing the SUBJECT swaps in a two-button token group
                      that is not a textAngular toolbar at all. The body strip is what is left. */}
                  {isEditingSignature ? (
                    <RichTextToolbar toolbar={SIGNATURE_TOOLBAR} name="signatureToolbar" />
                  ) : isSubjectInFocus ? (
                    <div className="btn-group">
                      <button
                        type="button"
                        className="btn btn-default"
                        tabIndex={-1}
                        title="Insert candidate/contact first name"
                      >
                        {'{First name}'}
                      </button>
                      {alertId === undefined ? null : (
                        <button type="button" className="btn btn-default" tabIndex={-1} title="Insert hunter name">
                          {'{Hunter}'}
                        </button>
                      )}
                    </div>
                  ) : (
                    <RichTextToolbar
                      toolbar={alertId === undefined ? BODY_ALERTLESS_TOOLBAR : BODY_TOOLBAR}
                      name="messageToolbar"
                    />
                  )}
                </div>
                </div>,
              )}
            </div>
          </div>
          <div className="extension-candidate-messages-footer">
            <div className="extension-candidate-message-template-cta-container">
              {/* ⚠️ "Save as new template" IS DISABLED ON AN EMPTY COMPOSER, by the same rule
                  that fades Send: `extension-candidate-messages-templates-disabled` sets
                  `opacity: 0.5` and `pointer-events: none` on the span. It reads as a colour
                  difference in a diff and it is a state — there is nothing to save yet. */}
              <div className={canSaveTemplate ? undefined : 'extension-candidate-messages-templates-disabled'}>
                <div>
                  <span>Save as new template</span>
                </div>
              </div>
            </div>
            {/* ⚠️ THE INVALID-PARAMETER LINE IS UPSTREAM'S AND THIS PORT HAD DROPPED IT.
                `extension-alert-candidates-messages.tpl.html:156` shows it whenever
                `invalidParameters.length > 0`, between the template CTA and Send — the count
                was already a prop here, used to disable the button and never to say why. A
                Send that fades with no explanation is the state this label exists to prevent.
                `ng-show` rather than `ng-if` upstream, which is the same zero box when hidden. */}
            {(invalidParameterCount ?? 0) > 0 ? (
              <label className="control-label has-error extension-message-invalid-param-error">
                Unknown or invalid parameters in message/follow-up body.
              </label>
            ) : null}
            {/* The send's own outcome — see `error`. It borrows the class above rather than
                inventing one: that is upstream's footer error label, 11px with a 10px left
                margin, already in the ported stylesheet and already in the right place. The
                two cannot both be the whole story at once — an invalid parameter keeps
                `canSendMessage` false, so no send can be in flight — and showing both is
                better than hiding either. */}
            {error === undefined ? null : (
              <label className="control-label has-error extension-message-invalid-param-error">{error}</label>
            )}
            {/* ⚠️ THE DISABLED LOOK IS A CLASS ON THE WRAPPER, not the `disabled` attribute on
                the button — upstream's shape, and the reason every capture of this screen shows
                the faded orange: the composer opens with an empty subject and body, so
                `canSendMessage` is false and the class is on. Moving it to the attribute would
                change the rendering and the reference with it. */}
            {/* ⚠️ THE TOOLTIP IS ON THE WRAPPER, NOT THE BUTTON, and has to be: the disabled
                class sets `pointer-events: none` on the button, so a button can never be hovered
                in exactly the state the tooltip explains. Upstream's `uib-tooltip` sits here for
                that reason — `tooltip-placement="top-right"`, `tooltip-append-to-body`. */}
            <div
              ref={sendHost}
              className={[
                'extension-candidate-messages-send',
                sendable ? '' : 'extension-candidate-messages-send-disabled',
              ]
                .filter(Boolean)
                .join(' ')}
              onMouseEnter={sendTooltip.onMouseEnter}
              onMouseLeave={sendTooltip.onMouseLeave}
            >
              {/* ⚠️ THE GUARD IS ON THE HANDLER TOO, NOT ONLY ON THE CLASS. A class cannot stop
                  a click, and this one mails real candidates — so the precondition is checked
                  again here rather than trusted from the styling. */}
              <button
                type="button"
                onClick={() => {
                  onSendButtonClick?.()
                  if (!sendable || sending) return
                  onSend?.()
                }}
              >
                Send
              </button>
            </div>
            {/* A SIBLING IN THE REACT TREE, though its DOM is on `<body>`: React routes
                enter/leave through the component tree, so as a child of the wrapper the pointer
                could move onto the popup without "leaving" Send, and it would stay open where
                uib hides it. */}
            {sendTooltip.open ? (
              <UibTooltipPopup host={sendHost} placement="top-right">
                {sendCopy}
              </UibTooltipPopup>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )

  // Upstream's modal service appends the dialogue outside the composer; it is fixed-position,
  // so rendering it beside the content draws the same thing.
  const clearDialogue = !isConfirmingClear ? null : (
    <GenericDialogue
      confirmLabel="Remove"
      onConfirm={() => {
        setConfirmingClear(false)
        void writeSignature(true)
      }}
      onCancel={() => {
        setConfirmingClear(false)
      }}
    >
      <span>Remove your signature? It will no longer be added to your messages.</span>
    </GenericDialogue>
  )

  return createElement(
    entity === 'clientContacts' ? 'extension-alert-client-contact-messages' : 'extension-alert-candidates-messages',
    flexBasis === undefined ? null : { style: { flexBasis: `${String(flexBasis)}px` } },
    content,
    clearDialogue,
  )
}
